import { createContext, useContext } from 'react';
import {
  keepPreviousData, useMutation, useQuery, useQueryClient,
} from '@tanstack/react-query';
import type { Order, PurchaseTask, MarketQuote, Shipment } from '@prody/shared';
import type {
  AssignSupplierInput, ListParams, NewOrderInput, OrderFilters, ProdyApi,
} from './api';

/* ------------------------------------------------------------------ *
 * Query keys are tenant-first: ['biz', businessId, entity, …].
 *
 * That single convention is what makes multi-tenancy safe on the
 * client. Switching business changes the second segment, so nothing
 * from the previous tenant can be served from cache — no manual
 * `queryClient.clear()` that someone forgets to call from the one new
 * code path added in November.
 * ------------------------------------------------------------------ */

export const ApiContext = createContext<ProdyApi | null>(null);

export const useApi = (): ProdyApi => {
  const api = useContext(ApiContext);
  if (!api) throw new Error('useApi must be used inside an ApiProvider');
  return api;
};

export const keys = {
  biz: (businessId: string) => ['biz', businessId] as const,
  orders: (businessId: string, filters?: OrderFilters) =>
    ['biz', businessId, 'orders', filters ?? {}] as const,
  order: (businessId: string, orderId: string) =>
    ['biz', businessId, 'order', orderId] as const,
  quotes: (businessId: string, day: string) =>
    ['biz', businessId, 'quotes', day] as const,
  purchaseTasks: (businessId: string, day: string) =>
    ['biz', businessId, 'purchase-tasks', day] as const,
  shipments: (businessId: string, date: string) =>
    ['biz', businessId, 'shipments', date] as const,
  routes: (businessId: string, date: string) =>
    ['biz', businessId, 'routes', date] as const,
  products: (businessId: string, params?: ListParams) =>
    ['biz', businessId, 'products', params ?? {}] as const,
  parties: (businessId: string, params?: ListParams & { role?: string }) =>
    ['biz', businessId, 'parties', params ?? {}] as const,
  party: (businessId: string, partyId: string) =>
    ['biz', businessId, 'party', partyId] as const,
  stock: (businessId: string, params?: ListParams) =>
    ['biz', businessId, 'stock', params ?? {}] as const,
  invoices: (businessId: string, params?: object) =>
    ['biz', businessId, 'invoices', params ?? {}] as const,
  payments: (businessId: string, params?: ListParams) =>
    ['biz', businessId, 'payments', params ?? {}] as const,
  balances: (businessId: string, direction: string) =>
    ['biz', businessId, 'balances', direction] as const,
  threads: (businessId: string, params?: ListParams) =>
    ['biz', businessId, 'threads', params ?? {}] as const,
  messages: (businessId: string, threadId: string) =>
    ['biz', businessId, 'messages', threadId] as const,
  appointments: (businessId: string, from: string, to: string) =>
    ['biz', businessId, 'appointments', from, to] as const,
  team: (businessId: string) => ['biz', businessId, 'team'] as const,
};

/* ---------------- Reads ---------------- */

/* List queries keep the previous page on screen while a new filter
   loads — switching the day must never flash an empty table. */

export const useOrders = (businessId: string, filters?: OrderFilters) => {
  const api = useApi();
  return useQuery({
    queryKey: keys.orders(businessId, filters),
    queryFn: () => api.listOrders(businessId, filters),
    placeholderData: keepPreviousData,
  });
};

export const useOrder = (businessId: string, orderId: string | null) => {
  const api = useApi();
  return useQuery({
    queryKey: keys.order(businessId, orderId ?? ''),
    queryFn: () => api.getOrder(businessId, orderId!),
    enabled: Boolean(orderId),
  });
};

export const useQuotes = (businessId: string, tradingDay: string) => {
  const api = useApi();
  return useQuery({
    queryKey: keys.quotes(businessId, tradingDay),
    queryFn: () => api.listQuotes(businessId, tradingDay),
    placeholderData: keepPreviousData,
  });
};

export const usePurchaseTasks = (businessId: string, tradingDay: string) => {
  const api = useApi();
  return useQuery({
    queryKey: keys.purchaseTasks(businessId, tradingDay),
    queryFn: () => api.listPurchaseTasks(businessId, tradingDay),
    placeholderData: keepPreviousData,
  });
};

export const useShipments = (businessId: string, date: string) => {
  const api = useApi();
  return useQuery({
    queryKey: keys.shipments(businessId, date),
    queryFn: () => api.listShipments(businessId, date),
    placeholderData: keepPreviousData,
  });
};

export const useRoutes = (businessId: string, date: string) => {
  const api = useApi();
  return useQuery({ queryKey: keys.routes(businessId, date), queryFn: () => api.listRoutes(businessId, date), placeholderData: keepPreviousData });
};

export const useProducts = (businessId: string, params?: ListParams) => {
  const api = useApi();
  return useQuery({ queryKey: keys.products(businessId, params), queryFn: () => api.listProducts(businessId, params), placeholderData: keepPreviousData });
};

export const useParties = (businessId: string, params?: ListParams & { role?: string }) => {
  const api = useApi();
  return useQuery({ queryKey: keys.parties(businessId, params), queryFn: () => api.listParties(businessId, params), placeholderData: keepPreviousData });
};

export const useParty = (businessId: string, partyId: string | null) => {
  const api = useApi();
  return useQuery({
    queryKey: keys.party(businessId, partyId ?? ''),
    queryFn: () => api.getParty(businessId, partyId!),
    enabled: Boolean(partyId),
  });
};

export const useStock = (businessId: string, params?: ListParams) => {
  const api = useApi();
  return useQuery({ queryKey: keys.stock(businessId, params), queryFn: () => api.listStock(businessId, params), placeholderData: keepPreviousData });
};

export const useInvoices = (
  businessId: string,
  params?: ListParams & { direction?: string; status?: string[] },
) => {
  const api = useApi();
  return useQuery({ queryKey: keys.invoices(businessId, params), queryFn: () => api.listInvoices(businessId, params), placeholderData: keepPreviousData });
};

export const usePayments = (businessId: string, params?: ListParams) => {
  const api = useApi();
  return useQuery({ queryKey: keys.payments(businessId, params), queryFn: () => api.listPayments(businessId, params), placeholderData: keepPreviousData });
};

export const useBalances = (businessId: string, direction: 'receivable' | 'payable') => {
  const api = useApi();
  return useQuery({
    queryKey: keys.balances(businessId, direction),
    queryFn: () => api.listBalances(businessId, direction),
  });
};

export const useThreads = (businessId: string, params?: ListParams) => {
  const api = useApi();
  return useQuery({ queryKey: keys.threads(businessId, params), queryFn: () => api.listThreads(businessId, params), placeholderData: keepPreviousData });
};

export const useMessages = (businessId: string, threadId: string | null) => {
  const api = useApi();
  return useQuery({
    queryKey: keys.messages(businessId, threadId ?? ''),
    queryFn: () => api.listMessages(businessId, threadId!),
    enabled: Boolean(threadId),
  });
};

export const useAppointments = (businessId: string, from: string, to: string) => {
  const api = useApi();
  return useQuery({
    queryKey: keys.appointments(businessId, from, to),
    queryFn: () => api.listAppointments(businessId, from, to),
  });
};

export const useTeam = (businessId: string) => {
  const api = useApi();
  return useQuery({ queryKey: keys.team(businessId), queryFn: () => api.listTeam(businessId) });
};

/* ---------------- Writes ----------------
   Every mutation applies optimistically and rolls back on failure.
   In a warehouse on a phone with two bars of signal, waiting for a
   round-trip before the row changes is the difference between the
   picker trusting the app and the picker keeping a paper list. */

export const useUpdateOrder = (businessId: string) => {
  const api = useApi();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ orderId, patch }: { orderId: string; patch: Partial<Order> }) =>
      api.updateOrder(businessId, orderId, patch),
    onMutate: async ({ orderId, patch }) => {
      await qc.cancelQueries({ queryKey: keys.order(businessId, orderId) });
      const previous = qc.getQueryData<Order>(keys.order(businessId, orderId));
      if (previous) {
        qc.setQueryData<Order>(keys.order(businessId, orderId), { ...previous, ...patch });
      }
      return { previous };
    },
    onError: (_err, { orderId }, ctx) => {
      if (ctx?.previous) qc.setQueryData(keys.order(businessId, orderId), ctx.previous);
    },
    onSettled: (_data, _err, { orderId }) => {
      void qc.invalidateQueries({ queryKey: keys.order(businessId, orderId) });
      void qc.invalidateQueries({ queryKey: ['biz', businessId, 'orders'] });
    },
  });
};

export const useCreateOrder = (businessId: string) => {
  const api = useApi();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: NewOrderInput) => api.createOrder(businessId, input),
    onSuccess: (order) => {
      qc.setQueryData<Order>(keys.order(businessId, order.id), order);
    },
    onSettled: () => {
      void qc.invalidateQueries({ queryKey: ['biz', businessId, 'orders'] });
    },
  });
};

/** One product, one day, one supplier — written onto every open line. */
export const useAssignSupplier = (businessId: string) => {
  const api = useApi();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: AssignSupplierInput) => api.assignSupplier(businessId, input),
    onSettled: () => {
      void qc.invalidateQueries({ queryKey: ['biz', businessId, 'orders'] });
      void qc.invalidateQueries({ queryKey: ['biz', businessId, 'order'] });
    },
  });
};

/** Optimistic against the day's shipment list — the van does not wait. */
export const useUpdateShipment = (businessId: string, date: string) => {
  const api = useApi();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ shipmentId, patch }: { shipmentId: string; patch: Partial<Shipment> }) =>
      api.updateShipment(businessId, shipmentId, patch),
    onMutate: async ({ shipmentId, patch }) => {
      const key = keys.shipments(businessId, date);
      await qc.cancelQueries({ queryKey: key });
      const previous = qc.getQueryData<Shipment[]>(key);
      if (previous) {
        qc.setQueryData<Shipment[]>(
          key,
          previous.map((s) => (s.id === shipmentId ? { ...s, ...patch } : s)),
        );
      }
      return { previous };
    },
    onError: (_err, _vars, ctx) => {
      if (ctx?.previous) qc.setQueryData(keys.shipments(businessId, date), ctx.previous);
    },
    onSettled: () => {
      void qc.invalidateQueries({ queryKey: ['biz', businessId, 'shipments'] });
      void qc.invalidateQueries({ queryKey: ['biz', businessId, 'routes'] });
    },
  });
};

export const useGeneratePurchaseTasks = (businessId: string, tradingDay: string) => {
  const api = useApi();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => api.generatePurchaseTasks(businessId, tradingDay),
    onSuccess: (next) => {
      qc.setQueryData<PurchaseTask[]>(keys.purchaseTasks(businessId, tradingDay), next);
    },
    onSettled: () => {
      void qc.invalidateQueries({ queryKey: ['biz', businessId, 'purchase-tasks'] });
    },
  });
};

export const useUpdateQuote = (businessId: string, tradingDay: string) => {
  const api = useApi();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ quoteId, patch }: { quoteId: string; patch: Partial<MarketQuote> }) =>
      api.updateQuote(businessId, quoteId, patch),
    onMutate: async ({ quoteId, patch }) => {
      const key = keys.quotes(businessId, tradingDay);
      await qc.cancelQueries({ queryKey: key });
      const previous = qc.getQueryData<MarketQuote[]>(key);
      if (previous) {
        qc.setQueryData<MarketQuote[]>(
          key,
          previous.map((q) => (q.id === quoteId ? { ...q, ...patch } : q)),
        );
      }
      return { previous };
    },
    onError: (_err, _vars, ctx) => {
      if (ctx?.previous) qc.setQueryData(keys.quotes(businessId, tradingDay), ctx.previous);
    },
    onSettled: () => {
      void qc.invalidateQueries({ queryKey: keys.quotes(businessId, tradingDay) });
    },
  });
};

export const useUpdatePurchaseTask = (businessId: string, tradingDay: string) => {
  const api = useApi();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ taskId, patch }: { taskId: string; patch: Partial<PurchaseTask> }) =>
      api.updatePurchaseTask(businessId, taskId, patch),
    onMutate: async ({ taskId, patch }) => {
      const key = keys.purchaseTasks(businessId, tradingDay);
      await qc.cancelQueries({ queryKey: key });
      const previous = qc.getQueryData<PurchaseTask[]>(key);
      if (previous) {
        qc.setQueryData<PurchaseTask[]>(
          key,
          previous.map((t) => (t.id === taskId ? { ...t, ...patch } : t)),
        );
      }
      return { previous };
    },
    onError: (_err, _vars, ctx) => {
      if (ctx?.previous) qc.setQueryData(keys.purchaseTasks(businessId, tradingDay), ctx.previous);
    },
    onSettled: () => {
      void qc.invalidateQueries({ queryKey: keys.purchaseTasks(businessId, tradingDay) });
      void qc.invalidateQueries({ queryKey: ['biz', businessId, 'orders'] });
    },
  });
};

export const useSendMessage = (businessId: string, threadId: string) => {
  const api = useApi();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ body, channel }: { body: string; channel: string }) =>
      api.sendMessage(businessId, threadId, body, channel),
    onSettled: () => {
      void qc.invalidateQueries({ queryKey: keys.messages(businessId, threadId) });
      void qc.invalidateQueries({ queryKey: ['biz', businessId, 'threads'] });
    },
  });
};
